import type { BaseSyntheticEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import {
  AS_NEW_BLOCK_PAYLOAD_KEYS,
  CREATE_LIBRARY_BLOCK_PAYLOAD_KEYS,
  CREATE_RESOURCE_BLOCK_PAYLOAD_KEYS,
  UPDATE_LIBRARY_BLOCK_PAYLOAD_KEYS,
  UPDATE_LIBRARY_BLOCK_PERMISSIONS_KEYS,
  UPDATE_RESOURCE_BLOCK_PAYLOAD_KEYS,
  UPDATE_TEMPLATE_BLOCK_PERMISSIONS_KEYS,
  useBlockEdit,
  useBlockEditFormStore,
  validateRequiredDatapoints,
} from '@ContractBuilder/modules/block-edit';
import { useBlockActions } from '@ContractBuilder/modules/block-edit/hooks/use-block-actions';
import { useEntityStore } from '@ContractBuilder/store';
import { useUIStore } from '@ContractBuilder/store/ui.store';
import { isNonNullish } from '@root/helpers';
import { performVisibilityConfigUpdates } from '@root/helpers/blocks/perform-visibility-config-updates';
import type { WithRequired } from '@root/helpers/object/types';
import { upliftVariation } from '@root/helpers/variations';
import { isBlocksPath, isClausesPath, isEndorsementViewPath, isTemplatePath } from '@utils/app-paths';
import { isEmpty, pick } from 'lodash-es';
type BlockEditSubmitHandler = (
  state: BlockEditFormState,
  event?: BaseSyntheticEvent,
) => Promise<boolean | string> | boolean;

type BlockEditSubmitAsNewHandler = (
  state: BlockEditFormState,
  isChildBlock?: boolean,
  event?: BaseSyntheticEvent,
) => Promise<boolean | string> | boolean;

export const useOnBlockEditSubmit = () => {
  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { currentBlock } = useBlockEditFormStore(({ currentBlock }) => ({
    currentBlock,
  }));
  const { setBlockUsedInTemplatesModalVisibility } = useUIStore(({ setBlockUsedInTemplatesModalVisibility }) => ({
    setBlockUsedInTemplatesModalVisibility,
  }));

  const {
    addBlockFromLibraryToTemplate,
    createBlock,
    createBlockInLibrary,
    createSummaryBlock,
    replaceBlockForBlockInLibrary,
    updateBlock,
    updateBlockInLibrary,
    updateSummaryBlock,
  } = useBlockActions();
  const { onMandatoryDatapointsModalOpen, resetState } = useBlockEdit();
  const isEndorsementView = isEndorsementViewPath();

  const isBlocks = isBlocksPath() || isClausesPath();
  const isTemplate = isTemplatePath();
  const isAdmin = isTemplate || isBlocks;
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleResetState = useCallback(() => {
    if (!isBlocks) {
      return resetState();
    }
  }, [isBlocks, resetState]);

  const performModalTriggerChecks = useCallback(
    (parsedFormValues: BlockEditFormState) => {
      const isBlockUsedInTemplatesModalOpen = useUIStore.getState().isBlockUsedInTemplatesModalOpen;
      const missingMandatoryDatapoints = validateRequiredDatapoints(currentBlock, parsedFormValues);

      if (!isAdmin && missingMandatoryDatapoints.list?.length) {
        onMandatoryDatapointsModalOpen();
        return false;
      }

      const { usedInTemplates = [] } = parsedFormValues;
      const notCurrentTemplate = ({ id }: { id: string }) => id !== submission?.id;

      const isUsedElsewhere = usedInTemplates.filter(notCurrentTemplate).length > 0;

      if (isAdmin && isUsedElsewhere && !isBlockUsedInTemplatesModalOpen) {
        setBlockUsedInTemplatesModalVisibility(true);
        return false;
      }

      return true;
    },
    [currentBlock, isAdmin, onMandatoryDatapointsModalOpen, setBlockUsedInTemplatesModalVisibility, submission?.id],
  );

  const getBlockPermissionsPayload = useCallback(
    (values: BlockEditFormState) => {
      if (isBlocks) {
        return pick(values, UPDATE_LIBRARY_BLOCK_PERMISSIONS_KEYS);
      }

      return pick(values, UPDATE_TEMPLATE_BLOCK_PERMISSIONS_KEYS);
    },
    [isBlocks],
  );

  const handleCreateBlock = useCallback(
    async (values: BlockEditFormState) => {
      const { canDeleteOnTemplate, order = 0 } = values;

      if (isEndorsementView) {
        await createSummaryBlock({ ...values, order: order + 1 });
        return;
      }

      if (isAdmin) {
        const createPayload = pick(values, CREATE_LIBRARY_BLOCK_PAYLOAD_KEYS);
        const blockLibraryId = await createBlockInLibrary(createPayload);

        if (blockLibraryId && isTemplate) {
          await addBlockFromLibraryToTemplate({
            canDeleteOnTemplate,
            selectedVariationId: values.selectedVariationId,
            blockLibraryId,
            sectionId: values.section_id,
            order: order + 1,
          });
        }
      } else {
        const createPayload = pick(values, CREATE_RESOURCE_BLOCK_PAYLOAD_KEYS);
        await createBlock({ ...createPayload, content: values.content ?? '' }, order);
      }
    },
    [
      addBlockFromLibraryToTemplate,
      createBlock,
      createBlockInLibrary,
      createSummaryBlock,
      isAdmin,
      isEndorsementView,
      isTemplate,
    ],
  );

  const handleUpdateBlock = useCallback(
    async (values: WithRequired<BlockEditFormState, 'id'>) => {
      const blockPermissionsPayload = getBlockPermissionsPayload(values);
      const isBlockFromLibrary = Boolean(isAdmin && values?.blockLibraryId);
      const visibilityParsedPayload = performVisibilityConfigUpdates(values);

      const { id } = values;

      if (isEndorsementView) {
        await updateSummaryBlock({ ...values, blockId: id });
        return;
      }

      if (isBlockFromLibrary || isBlocks) {
        const updatePayload = {
          ...pick(visibilityParsedPayload, UPDATE_LIBRARY_BLOCK_PAYLOAD_KEYS),
          variations: visibilityParsedPayload.variations ?? [],
          teams: visibilityParsedPayload.teams ?? [],
          class_of_business: visibilityParsedPayload.class_of_business ?? [],
        };
        await updateBlockInLibrary(visibilityParsedPayload.blockLibraryId ?? id, updatePayload, id);

        if (isTemplate) {
          await updateBlock({
            ...blockPermissionsPayload,
            id,
            selectedVariationId: visibilityParsedPayload.selectedVariationId,
            section_id: visibilityParsedPayload.section_id,
            visibility: visibilityParsedPayload.visibility,
            teams: visibilityParsedPayload.teams,
          });
        }

        setBlockUsedInTemplatesModalVisibility(false);
      } else {
        const updatePayload = {
          ...pick(visibilityParsedPayload, UPDATE_RESOURCE_BLOCK_PAYLOAD_KEYS),
          id,
          variations: visibilityParsedPayload.variations ?? [],
        };
        await updateBlock(updatePayload);
      }
    },
    [
      getBlockPermissionsPayload,
      isAdmin,
      isBlocks,
      isEndorsementView,
      isTemplate,
      setBlockUsedInTemplatesModalVisibility,
      updateBlock,
      updateBlockInLibrary,
      updateSummaryBlock,
    ],
  );

  const onSubmit: BlockEditSubmitHandler = useCallback(
    async (state) => {
      const isLoading = useEntityStore.getState().isLoading;

      if (isLoading || isSubmittingRef.current) {
        return false;
      }

      isSubmittingRef.current = true;
      setIsSubmitting(true);

      try {
        const checksPassed = performModalTriggerChecks(state);

        if (!checksPassed) {
          return false;
        }

        const parsedFormValues = upliftVariation(state, state.selectedVariationId);
        const { id } = parsedFormValues;

        const blockExists = !isEmpty(id) && isNonNullish(id);

        if (!blockExists) {
          await handleCreateBlock(parsedFormValues);
        } else {
          await handleUpdateBlock({ ...parsedFormValues, id });
        }

        handleResetState();
        return true;
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line -- We don't care about functions changing
    [isAdmin, submission?.id],
  );

  const onSubmitAsNew: BlockEditSubmitAsNewHandler = useCallback(async (state, isChildBlock = true) => {
    if (!state) {
      return true;
    }

    if (!isAdmin || !state.id) {
      return true;
    }

    if (isSubmittingRef.current) {
      return false;
    }

    isSubmittingRef.current = true;

    try {
      const originalBlock = useBlockEditFormStore.getState().currentBlock;
      const isFreshImport =
        Boolean(state.last_imported_at) && state.last_imported_at !== originalBlock?.last_imported_at;
      const parentBlockId = state.parentBlockId ?? state.blockLibraryId ?? state.id;

      const newBlockPayload = {
        ...pick(state, AS_NEW_BLOCK_PAYLOAD_KEYS),
        name: state.name,
        parentBlockId: isChildBlock ? parentBlockId : undefined,
        ...(isFreshImport && { last_imported_at: state.last_imported_at }),
      };

      const { section_id: sectionId, canDeleteOnTemplate } = state;
      const order = submission?.sections
        ?.find((section) => section.id === state.section_id)
        ?.layout?.findIndex((blockId) => blockId === state.id);

      const blockLibraryId = await createBlockInLibrary(newBlockPayload);

      if (isTemplate) {
        await replaceBlockForBlockInLibrary(state.id, {
          ...state,
          sectionId,
          blockLibraryId,
          order,
          canDeleteOnTemplate,
        });
      }

      handleResetState();
      return blockLibraryId;
    } finally {
      isSubmittingRef.current = false;
    }
    // eslint-disable-next-line -- We don't care about function-like dependencies of this hook
  }, []);

  return { isSubmitting, onSubmit, onSubmitAsNew };
};
